import {
  downloadFile,
  GetImpactReportCsvRequestBody,
  ImpactType,
  User,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { object, string } from "yup";
import { Button } from "../Button";
import { DialogModal } from "../DialogModal";
import { Typography } from "../Typography";
import { useForm, useIsMobile } from "../utils";
import DownloadIcon from "@ecologi/assets/icons/download-arrow.svg";
import { useMutation } from "react-query";
import { TextField } from "../TextField";
import { toast } from "react-toastify";

const ContentContainer = styled.div`
  padding: 26px 24px 24px 24px;
  @media ${(props) => props.theme.bp.tabletDown} {
    padding: 0;
  }
`;

const Divider = styled.hr`
  height: 2px;
  background-color: ${(props) => props.theme.colors.charcoal};
  border: none;
  width: 14px;
  margin-top: 52px;
`;

const ParentProfileNotice = styled(Typography)`
  margin-top: 48px;
`;

const DateInputContainer = styled.div`
  display: flex;
  align-items: start;
  flex-wrap: wrap;
  justify-content: space-between;
  @media ${(props) => props.theme.bp.tabletDown} {
    flex-direction: column;
    align-items: center;
    gap: 32px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 36px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 32px;
  @media ${(props) => props.theme.bp.tabletDown} {
    flex-direction: column-reverse;
  }
`;

const StyledTextField = styled(TextField)`
  width: 264px;
  @media ${(props) => props.theme.bp.tabletDown} {
    width: 100%;
  }
`;

const StyledHeader = styled(Typography)`
  margin-bottom: 18px;
`;

const TitleContainer = styled.div`
  margin-bottom: 44px;
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  getImpactReportCsv: (args: GetImpactReportCsvRequestBody) => Promise<string>;
  accessToken: string;
};

const createSchema = (user: User) =>
  object().shape({
    startDate: string()
      .required("From date is required")
      .test("is-valid-date", "Start date is invalid", (value) => {
        return !value || Boolean(DateTime.fromISO(value).isValid);
      })
      .test(
        "is-after-created-at",
        `From date must be after account creation ${DateTime.fromJSDate(
          user.createdAt
        ).toFormat("d LLL yyyy")}`,
        (value) => {
          const createdAt = DateTime.fromJSDate(user.createdAt).startOf("day");
          const startDate = DateTime.fromISO(value || "").startOf("day");
          return startDate >= createdAt;
        }
      )
      .test(
        "is-before-end-date",
        "From date must be before to date",
        function (value) {
          const { endDate } = this.parent;
          if (!endDate || !value) return true;
          const startDate = DateTime.fromISO(value).startOf("day");
          const endDateObj = DateTime.fromISO(endDate).startOf("day");
          return startDate <= endDateObj;
        }
      )
      .nullable(),
    endDate: string()
      .required("To date is required")
      .test("is-valid-date", "To date is invalid", (value) => {
        return !value || Boolean(DateTime.fromISO(value).isValid);
      })
      .test(
        "is-before-today",
        "To date must be on or before today",
        (value) => {
          const today = DateTime.local().startOf("day");
          const endDate = DateTime.fromISO(value || "").startOf("day");
          return !value || endDate <= today;
        }
      )
      .test(
        "is-after-start-date",
        "To date must be after from date",
        function (value) {
          const { startDate } = this.parent;
          if (!startDate || !value) return true;
          const endDate = DateTime.fromISO(value).startOf("day");
          const startDateObj = DateTime.fromISO(startDate).startOf("day");
          return endDate >= startDateObj;
        }
      )
      .nullable(),
  });

const downloadIntro =
  "Generate a CSV spreadsheet of the impact you have funded over a specific period of time. View impact by date, impact type and projects.";
const feedbackIntro =
  "Help us improve this feature by providing your feedback. You’ll be redirected to a Google form (external link).";
const downloadHeader = "Generate a report";
const feedbackHeader = "Your download was successful";
export const parentProfileNotice =
  "*Please note, this report does not include impact funded on sub-profiles. To view sub-profile impact, please download this report logged in from a sub-profile.";

export const CreateFundReportModal = ({
  isOpen,
  onClose,
  user,
  getImpactReportCsv,
  accessToken,
}: Props) => {
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const handleFeedback = () => {
    window.open(
      "https://forms.gle/bBR5idNnSGxh9NcA8",
      "_blank",
      "noopener noreferrer"
    );
    onClose();
  };

  const form = useForm({
    validationSchema: createSchema(user),
    validateOnBlur: true,
    validateOnChange: false,
    initialValues: {
      startDate: DateTime.fromJSDate(user.createdAt).toISODate(),
      endDate: DateTime.local().toISODate(),
    },
    onSubmit: async (formValues) => {
      await onGetImpactReportCsv({
        startDate: formValues.startDate,
        userId: user.userId,
        endDate: formValues.endDate,
        apiOnly: false,
        impactTypes: [
          ImpactType.TREES,
          ImpactType.CARBON_OFFSETS,
          ImpactType.HABITAT_RESTORATION,
          ImpactType.CARBON_REMOVAL,
        ],
        accessToken,
      });
    },
  });

  const [onGetImpactReportCsv] = useMutation(getImpactReportCsv, {
    onError: () => {
      toast.error(
        "An issue occurred when creating your report. Please try again and if the issue persists, contact support."
      );
    },
    onSuccess: async (csvText: string) => {
      downloadFile(csvText, `${user.username}.csv`, "text/csv");
      setHasDownloaded(true);
    },
  });

  const isMobile = useIsMobile();

  const {
    isSubmitting,
    handleSubmit,
    isValid,
    errors: { endDate: endDateError, startDate: startDateError },
  } = form;

  return (
    <DialogModal isOpen={isOpen} onClose={onClose}>
      <ContentContainer>
        <TitleContainer>
          <StyledHeader use="dashboardTitle2">
            {hasDownloaded ? feedbackHeader : downloadHeader}
          </StyledHeader>
          <Typography use="body1">
            {hasDownloaded ? feedbackIntro : downloadIntro}
          </Typography>
        </TitleContainer>
        {!hasDownloaded ? (
          <>
            <DateInputContainer>
              <StyledTextField
                {...form.getFieldProps("startDate", "From")}
                type="date"
                errorText={startDateError?.toString()}
                min={DateTime.fromJSDate(user.createdAt).toISODate()}
                max={
                  form.getFieldProps("endDate", "To").value ||
                  DateTime.local().toISODate()
                }
              />
              {!isMobile && <Divider />}
              <StyledTextField
                {...form.getFieldProps("endDate", "To")}
                type="date"
                errorText={endDateError?.toString()}
                min={
                  form.getFieldProps("startDate", "From").value ||
                  DateTime.fromJSDate(user.createdAt).toISODate()
                }
                max={DateTime.local().toISODate()}
              />
            </DateInputContainer>
            <ButtonContainer>
              <Button
                onClick={onClose}
                text="Cancel"
                textOnlyWithBorder
                fullWidth={isMobile}
              />
              <Button
                darkGreen
                text="Download report"
                icon={DownloadIcon}
                onClick={() => handleSubmit()}
                loading={isSubmitting}
                fullWidth={isMobile}
                disabled={!isValid}
              />
            </ButtonContainer>
            {user.isParentProfile && (
              <ParentProfileNotice use="dashboardMicroCopyRegular">
                {parentProfileNotice}
              </ParentProfileNotice>
            )}
          </>
        ) : (
          <ButtonContainer>
            <Button
              onClick={onClose}
              text="Not now"
              textOnlyWithBorder
              fullWidth={isMobile}
            />
            <Button
              darkGreen
              text="Go to feedback form"
              fullWidth={isMobile}
              onClick={handleFeedback}
            />
          </ButtonContainer>
        )}
      </ContentContainer>
    </DialogModal>
  );
};
